<script>
import {useDashboard} from '@mixins/rmDashboard';
import GlobalStats from '@views/revenuemanagement/parts/GlobalStats.vue';
import {defineAsyncComponent, provide} from 'vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import IndicatorCard from '@views/revenuemanagement/parts/IndicatorCard.vue';
import revenueManagementMixin from '@mixins/revenueManagementMixin';
import AkShimmerGroup from '@components/v2/general/AkShimmerGroup.vue';
import moment from 'moment';
import IndicatorGroup from '@views/revenuemanagement/parts/IndicatorGroup.vue';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';
import GaFilter from '@components/v2/filter/GaFilter.vue';
import AkCalendar from '@components/v2/input/AkCalendar.vue';
import rmDateTypeConst from '@mixins/const/rmDateTypeConst';
import {error} from '@splidejs/splide/src/js/utils';
import SiteDistributionIndicatorCard from '@views/revenuemanagement/parts/SiteDistributionIndicatorCard.vue';
import RatingStar from '@views/revenuemanagement/RatingStar.vue';
import AkInputText from '@components/v2/input/AkInputText.vue';
import utilsMixin from '@mixins/utilsMixin';
import MainTabMixin from '@mixins/mainTabMixin';
import GaLayoutEmptyTab from '@components/v2/general/GaLayoutEmptyTab.vue';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';
import RmFilterDatePeriods from '@views/revenuemanagement/parts/filter/RmFilterDatePeriods.vue';

const RmFilterMainDate = defineAsyncComponent(
  () => import('@views/revenuemanagement/parts/filter/RmFilterMainDate.vue'),
);
const RmFilterComparisonDate = defineAsyncComponent(
  () => import('@views/revenuemanagement/parts/filter/RmFilterComparisonDate.vue'),
);
const RmDateTypeFilter = defineAsyncComponent(
  () => import('@views/revenuemanagement/parts/filter/RmDateTypeFilter.vue'),
);
const GaFilterAccommodation = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterAccommodation.vue'),
);
const GaFilterReservationSite = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterReservationSite.vue'),
);
const GaFilterOwner = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterOwner.vue'));
const GaFilterCompany = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterCompany.vue'));
const GaFilterOffer = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterOffer.vue'));
const GaFilterMarket = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterMarket.vue'));
const GaFilterTiers = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterTiers.vue'));
const GaFilterTags = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterTags.vue'));
const GaFilterHouseKeepers = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterHouseKeepers.vue'),
);
const GaFilterChargeDeCompte = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterChargeDeCompte.vue'),
);

export default {
  name: 'RMStats',
  mixins: [revenueManagementMixin, permissionsMixinComputed, rmDateTypeConst, utilsMixin, MainTabMixin],
  components: {
    AkButtonValidation,
    GaLayoutEmptyTab,
    AkInputText,
    RatingStar,
    SiteDistributionIndicatorCard,
    AkCalendar,
    GaFilter,
    IndicatorGroup,
    AkShimmerGroup,
    IndicatorCard,
    AkDropdown,
    GlobalStats,
  },
  head() {
    return {
      title: 'stats.title',
    };
  },
  setup(props) {
    const dashboard = useDashboard();
    const periodLabels = {main: props.labelMainPeriod, comparison: props.labelCompPeriod};
    provide('dashboard', dashboard);
    provide('periodLabels', periodLabels);
    return {...dashboard, dashboard, periodLabels};
  },
  data() {
    return {
      scrollIndices: {
        quality: 0,
        performances: 0,
        finance: 0,
        occupation: 0,
        reservations: 0,
        reviews: 0,
      },
      filter: {
        dateType: 'checkin',
        mainPeriod: {
          period: [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
          searchDate: new Date(),
          periodView: 'currentMonth',
          searchView: 'today',
        },
        comparisonPeriod: {
          period: [
            moment().subtract(1, 'year').startOf('month').toDate(),
            moment().subtract(1, 'year').endOf('month').toDate(),
          ],
          searchDate: moment().subtract(1, 'year').toDate(),
          periodView: 'mainMinusOneYear',
          searchView: 'oneYearAgo',
        },
        companyIds: null,
        ownerIds: null,
        accommodationIds: null,
        mandatOffers: null,
        marcheIds: null,
        clusters: null,
        tagIds: null,
        reservationSites: null,
        housekeeperIds: null,
        chargeDeCompteIds: null,
        qualityInterval: 'weekly',
        performancesInterval: 'weekly',
      },
      monoFilter: null,
    };
  },
  mounted() {
    this.search();
  },
  methods: {
    getPeriodLabel(periodValue) {
      const labels = {
        yearToDate: 'Année à ce jour',
        currentYear: 'Année en cours',
        currentMonth: 'Mois en cours',
        nextMonth: 'Mois suivant',
        previousMonth: 'Mois précédent',
        next3Months: '3 prochains mois',
      };
      const view = periodValue?.periodView;
      if (view && view !== 'custom' && labels[view]) return labels[view];
      return this.formatDateRangeShort(periodValue?.period);
    },
    getComparisonPeriodLabel(mainValue, comparisonValue) {
      const compView = comparisonValue?.periodView;
      const mainView = mainValue?.periodView;

      if (compView === 'custom') return this.formatDateRangeShort(comparisonValue?.period);
      if (mainView === 'custom') return this.formatDateRangeShort(comparisonValue?.period);

      if (compView === 'mirror') return this.getPeriodLabel(mainValue);
      return `${this.getPeriodLabel(mainValue)} il y a un an`;
    },
    search(group) {
      if (this.filter.mainPeriod?.inlineSync) {
        this.filter.mainPeriod.searchDate = new Date();
        this.filter.comparisonPeriod.period = this.filter.mainPeriod.period.map((date) =>
          moment(date).subtract(1, 'year').toDate(),
        );
        this.filter.comparisonPeriod.searchDate = moment().subtract(1, 'year').toDate();
        delete this.filter.mainPeriod.inlineSync;
      }
      if (this.filter.comparisonPeriod.periodView === 'mirror')
        this.filter.comparisonPeriod.period = [...this.filter.mainPeriod.period];
      if (this.filter.comparisonPeriod.periodView === 'mainMinusOneYear')
        this.filter.comparisonPeriod.period = [
          ...this.filter.mainPeriod.period.map((date) => moment(date).subtract(1, 'year').toDate()),
        ];
      this.periodLabels.main = this.labelMainPeriod;
      this.periodLabels.comparison = this.labelCompPeriod;

      this.dashboard.fetchDashboard(this.filter, group);
    },
    resetFilter() {
      this.filter = {
        dateType: 'checkin',
        mainPeriod: {
          period: [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
          searchDate: new Date(),
          periodView: 'currentMonth',
          searchView: 'today',
        },
        comparisonPeriod: {
          period: [
            moment().subtract(1, 'month').startOf('month').toDate(),
            moment().subtract(1, 'month').endOf('month').toDate(),
          ],
          searchDate: moment().subtract(1, 'month').toDate(),
          periodView: 'mainMinusOneYear',
          searchView: 'oneYearAgo',
        },
        companyIds: null,
        ownerIds: null,
        accommodationIds: null,
        mandatOffers: null,
        marcheIds: null,
        clusters: null,
        tagIds: null,
        reservationSites: null,
        housekeeperIds: null,
        chargeDeCompteIds: null,
        qualityInterval: 'weekly',
        performancesInterval: 'weekly',
      };

      this.search();
    },
    handleGroupScroll(event, key) {
      const scrollPosition = event.target.scrollLeft;
      const width = event.target.clientWidth;
      this.scrollIndices[key] = Math.round(scrollPosition / width);
    },
    platformScroll(direction) {
      this.$refs.platformScrollContainer.scrollBy({
        left: direction * (Math.max(this.$refs.platformScrollContainer.offsetWidth, 1400) / 1.6),
        behavior: 'smooth',
      });
    },
    showPeriodsFilter() {
      this.monoFilter = 'periods';
      this.$nextTick(() => this.$refs.filters.showSidebar());
    },
    resetMonoFilter() {
      this.monoFilter = null;
    },
  },
  computed: {
    filterComponents() {
      if (this.monoFilter === 'periods') {
        return [
          {
            component: RmFilterDatePeriods,
            filterProperty: 'mainPeriod',
            showIcon: true,
          },
        ];
      }

      const options = [
        {
          component: RmDateTypeFilter,
          filterProperty: 'dateType',
          showIcon: true,
        },
        {
          component: RmFilterMainDate,
          filterProperty: 'mainPeriod',
          showIcon: true,
        },
        {
          component: RmFilterComparisonDate,
          filterProperty: 'comparisonPeriod',
          showIcon: true,
        },
        {
          component: GaFilterAccommodation,
          filterProperty: 'accommodationIds',
          showIcon: true,
          label: 'Logement',
        },
        {
          component: GaFilterReservationSite,
          filterProperty: 'reservationSites',
          showIcon: true,
          label: 'Plateforme',
        },
      ];
      if (this.isGasGav || this.isHousekeeper) {
        options.splice(3, 0, {
          component: GaFilterOwner,
          filterProperty: 'ownerIds',
          showIcon: true,
          label: 'Hôte',
        });
      }
      if (this.isGasGav) {
        options.splice(3, 0, {
          component: GaFilterCompany,
          filterProperty: 'companyIds',
          showIcon: true,
          label: 'Agence',
        });

        options.push(
          {
            component: GaFilterOffer,
            filterProperty: 'mandatOffers',
            showIcon: true,
            label: 'Formule',
          },
          {
            component: GaFilterMarket,
            filterProperty: 'marcheIds',
            showIcon: true,
            label: 'Marché',
          },
          {
            component: GaFilterTiers,
            filterProperty: 'clusters',
            showIcon: true,
            label: 'Tiers',
          },
          {
            component: GaFilterTags,
            filterProperty: 'tagIds',
            showIcon: true,
            label: 'Étiquettes',
          },
          {
            component: GaFilterHouseKeepers,
            filterProperty: 'housekeeperIds',
            showIcon: true,
            label: 'Concierge',
          },
        );
      }
      if (!this.isOwner) {
        options.push({
          component: GaFilterChargeDeCompte,
          filterProperty: 'chargeDeCompteIds',
          showIcon: true,
          label: 'Chargé de compte',
        });
      }
      return options;
    },
    labelMainPeriod() {
      return `${moment(this.filter.mainPeriod.period[0]).format('DD/MM/YYYY')} au ${moment(this.filter.mainPeriod.period[1]).format('DD/MM/YYYY')} en date du ${moment(this.filter.mainPeriod.searchDate).format('DD/MM/YYYY')}`;
    },
    labelCompPeriod() {
      return `${moment(this.filter.comparisonPeriod.period[0]).format('DD/MM/YYYY')} au ${moment(this.filter.comparisonPeriod.period[1]).format('DD/MM/YYYY')} en date du ${moment(this.filter.comparisonPeriod.searchDate).format('DD/MM/YYYY')}`;
    },
    financeIndice() {
      return this.isGasGav ? 5 : 4;
    },
    reservationIndice() {
      return this.isGasGav ? 5 : 3;
    },
  },
  watch: {
    labelMainPeriod(newVal) {
      this.periodLabels.main = newVal;
    },
    labelCompPeriod(newVal) {
      this.periodLabels.comparison = newVal;
    },
  },
};
</script>

<template>
  <template v-if="hasAccommodation || !isOwner">
    <global-stats />
    <div class="-mx-5">
      <div class="md:p-[48px] p-4 flex flex-col gap-[48px] mx-auto max-w-[1400px]">
        <GaFilter
          ref="filters"
          :filter-components="filterComponents"
          v-model:filter="filter"
          @apply="search"
          @reset="resetFilter"
          @close="resetMonoFilter">
          <template v-if="!this.$isMobile()" #inlineFilters="{internalFilter}">
            <AkDropdown
              :options="filterDateTypeConst"
              v-model="internalFilter.dateType.value"
              label="Filtrer par date de"
              class="!w-1/3"
              @value-change="this.$refs.filters.apply()" />
            <div class="flex flex-col gap-2.5 grow">
              <label for="" class="font-semibold text-grey-900 select-none">Afficher les données pour</label>
              <div
                class="grid xl:grid-cols-2 gap-x-4 gap-y-0.5 xl:px-4 px-2.5 xl:py-2 py-1 text-grey-900 font-medium rounded-md cursor-pointer select-none bg-grey-50"
                @click="showPeriodsFilter">
                <div class="flex gap-x-3 items-center xl:items-start xl:flex-col min-w-0">
                  <span class="text-sm font-semibold text-grey-900 shrink-0">Principale</span>
                  <div class="min-w-0 truncate">
                    <span class="text-sm leading-snug">{{ this.getPeriodLabel(internalFilter.mainPeriod.value) }}</span>
                    <span class="text-xs pl-1 font-normal text-grey-500">
                      en date du {{ this.formatDateIfNeeded(internalFilter.mainPeriod.value.searchDate) }}
                    </span>
                  </div>
                </div>
                <div class="flex gap-x-3 items-center xl:items-start xl:flex-col min-w-0">
                  <span class="text-sm font-semibold text-grey-900 shrink-0">Comparée</span>
                  <div class="min-w-0 truncate">
                    <span class="text-sm leading-snug">
                      {{
                        this.getComparisonPeriodLabel(
                          internalFilter.mainPeriod.value,
                          internalFilter.comparisonPeriod.value,
                        )
                      }}
                    </span>
                    <span class="text-xs pl-1 font-normal text-grey-500">
                      en date du {{ this.formatDateIfNeeded(internalFilter.comparisonPeriod.value.searchDate) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </GaFilter>

        <div v-if="prepareError" class="text-center text-red w-full">
          {{ prepareError }}
        </div>

        <AkShimmerGroup :loading="isPreparing">
          <IndicatorGroup
            icon="ga-stars_glow"
            label="Qualité"
            v-model:interval="filter.qualityInterval"
            :hideZeroLabels="true"
            @update:interval="search('quality')">
            <div class="relative">
              <div
                class="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 gap-4 pb-8 md:pb-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0"
                @scroll="handleGroupScroll($event, 'quality')">
                <!--    CARDS      -->
                <IndicatorCard
                  class="snap-center shrink-0 w-full md:w-auto"
                  technicalKey="REVIEW_COUNT"
                  icon="ga-icon ga-stars_triple"
                  label="Nombre d'avis"
                  graph-type="bar"
                  :hideZeroLabels="true"
                  icon-color="#002D6D"
                  icon-bg-color="#E9F0FF"
                  unit=" avis" />
                <IndicatorCard
                  class="snap-center shrink-0 w-full md:w-auto"
                  technicalKey="GLOBAL_RATING"
                  icon="ga-icon ga-star_sharp"
                  label="Note globale"
                  :hideZeroLabels="true"
                  icon-color="#3FB4E5"
                  icon-bg-color="#ECF7FE"
                  unit="/10">
                  <template #suffix="{indicator}">
                    <RatingStar :indicator="indicator * 10" />
                  </template>
                </IndicatorCard>
              </div>
              <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
                <div
                  v-for="i in 2"
                  :key="i"
                  class="h-2 w-2 rounded-full transition-colors duration-300"
                  :class="scrollIndices.quality === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
              </div>
            </div>

            <div class="gap-4 flex flex-col">
              <div class="flex items-center justify-between">
                <span class="px-3 py-1 text-grey-900 text-[18px] font-semibold title-border secondary before:h-full">
                  Note par plateforme
                </span>

                <div class="flex gap-4">
                  <router-link
                    class="bg-secondary-50 px-4 h-[48px] flex gap-1.5 rounded-md items-center text-secondary font-medium hover:text-secondary-600 focus:text-secondary-600"
                    :to="{name: 'reviews'}">
                    <i class="ga-icon ga-stars text-3xl"></i>
                    Avis
                  </router-link>

                  <div v-if="!this.$isMobile()" class="flex items-center gap-2 p-2">
                    <button
                      @click="platformScroll(-1)"
                      class="rounded-full border border-grey-100 hover:bg-grey-50 hover:border-grey-200 p-[5px] pb-1.5 pr-1.5">
                      <i class="ga-icon ga-chevron_left text-[22px]" />
                    </button>
                    <button
                      @click="platformScroll(1)"
                      class="rounded-full border border-grey-100 hover:bg-grey-50 hover:border-grey-200 p-[5px] pb-1.5 pl-1.5">
                      <i class="ga-icon ga-chevron_right text-[22px]" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="relative">
                <div
                  class="flex [--gap:1rem] gap-[var(--gap)] overflow-x-auto no-scrollbar platformScrollContainer snap-x snap-mandatory pb-8 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0"
                  ref="platformScrollContainer"
                  @scroll="handleGroupScroll($event, 'reviews')">
                  <!--     CARDS     -->
                  <IndicatorCard
                    class="md:snap-start snap-center shrink-0 w-full md:w-[calc((100%-var(--gap)*2)/3)]"
                    label="Guestadom"
                    unit="/5"
                    technicalKey="GUESTADOM_RATING">
                    <template #picto>
                      <i class="logo-site w-[32px] h-[32px] guestadom" />
                    </template>
                  </IndicatorCard>
                  <IndicatorCard
                    class="md:snap-start snap-center shrink-0 w-full md:w-[calc((100%-var(--gap)*2)/3)]"
                    label="Airbnb"
                    unit="/5"
                    technicalKey="AIRBNB_RATING">
                    <template #picto>
                      <i class="logo-site w-[32px] h-[32px] airbnb" />
                    </template>
                  </IndicatorCard>
                  <IndicatorCard
                    class="md:snap-start snap-center shrink-0 w-full md:w-[calc((100%-var(--gap)*2)/3)]"
                    label="Booking"
                    unit="/10"
                    technicalKey="BOOKING_RATING">
                    <template #picto>
                      <i class="logo-site w-[32px] h-[32px] booking" />
                    </template>
                  </IndicatorCard>
                  <IndicatorCard
                    class="md:snap-start snap-center shrink-0 w-full md:w-[calc((100%-var(--gap)*2)/3)]"
                    label="Abritel"
                    unit="/10"
                    technicalKey="ABRITEL_RATING">
                    <template #picto>
                      <i class="logo-site w-[32px] h-[32px] vrbo" />
                    </template>
                  </IndicatorCard>
                  <IndicatorCard
                    class="md:snap-start snap-center shrink-0 w-full md:w-[calc((100%-var(--gap)*2)/3)]"
                    label="PAP"
                    unit="/5"
                    technicalKey="PAP_RATING">
                    <template #picto>
                      <i class="logo-site w-[32px] h-[32px] pap" />
                    </template>
                  </IndicatorCard>
                  <IndicatorCard
                    class="md:snap-start snap-center shrink-0 w-full md:w-[calc((100%-var(--gap)*2)/3)]"
                    label="Holidu"
                    unit="/5"
                    technicalKey="HOLIDU_RATING">
                    <template #picto>
                      <i class="logo-site w-[32px] h-[32px] holidu" />
                    </template>
                  </IndicatorCard>
                </div>
                <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="h-2 w-2 rounded-full transition-colors duration-300"
                    :class="scrollIndices.reviews === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
                </div>
              </div>
            </div>
          </IndicatorGroup>

          <IndicatorGroup
            label="Performances"
            icon="ga-trend-up-graph"
            v-model:interval="filter.performancesInterval"
            @update:interval="search('performances')">
            <div class="relative">
              <div
                class="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8 md:pb-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0"
                @scroll="handleGroupScroll($event, 'performances')">
                <!--    CARDS      -->
                <IndicatorCard
                  class="snap-center shrink-0 w-fit md:w-auto"
                  icon="ga-coins"
                  icon-bg-color="#E9F0FF"
                  icon-color="#002D6D"
                  technicalKey="BUSINESS_VOLUME"
                  label="Volume d'affaires"
                  information="Correspond au total des loyers générés par vos réservations (hors ménage et taxe de séjour). C’est le chiffre d’affaires brut généré par votre logement sur les plateformes partenaires."
                  unit=" €" />
                <IndicatorCard
                  class="snap-center shrink-0 w-fit md:w-auto"
                  icon="ga-close_circle"
                  icon-bg-color="#FEF5EE"
                  icon-color="#F2743A"
                  technicalKey="CLOSURE_RATE"
                  label="Taux de fermeture"
                  information="Part des nuits où votre logement n’est pas disponible à la réservation (bloqué ou fermé). Un taux élevé peut limiter vos opportunités de réservation."
                  positive-delta-is-bad
                  unit=" %" />
                <IndicatorCard
                  class="snap-center shrink-0 w-fit md:w-auto"
                  icon="ga-3users"
                  icon-bg-color="#F1FCF5"
                  icon-color="#27AE60"
                  technicalKey="GUEST_OCCUPANCY_RATE"
                  label="Taux d'occupation voyageur"
                  information="Part des nuits réellement louées par des voyageurs, sur les nuits ouvertes à la réservation. C’est un indicateur clé de votre performance commerciale."
                  unit=" %" />
              </div>
              <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
                <div
                  v-for="i in 3"
                  :key="i"
                  class="h-2 w-2 rounded-full transition-colors duration-300"
                  :class="scrollIndices.performances === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
              </div>
            </div>

            <div class="gap-4 flex flex-col">
              <span class="px-3 py-1 text-grey-900 text-[18px] font-semibold title-border secondary before:h-full">
                Finance
              </span>
              <div class="relative">
                <div
                  class="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8 md:pb-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0"
                  @scroll="handleGroupScroll($event, 'finance')">
                  <!--    CARDS      -->
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="BUSINESS_VOLUME"
                    label="Volume d'affaires"
                    unit=" €"
                    information="Correspond au total des loyers générés par vos réservations (hors ménage et taxe de séjour). C’est le chiffre d’affaires brut généré par votre logement sur les plateformes partenaires." />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="AVERAGE_NIGHT_PRICE"
                    label="Prix moyen par nuit"
                    unit=" €"
                    information="Montant moyen payé par les voyageurs pour une nuit. Il permet de comprendre à quel prix votre logement est loué en moyenne." />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="REVPAR"
                    label="Revpar"
                    unit=" €"
                    information="Revenu généré par nuit disponible à la location. Cet indicateur combine votre taux de remplissage et votre prix moyen : il reflète la performance globale de votre logement." />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="HOST_REVENUE"
                    label="Revenu hôte"
                    unit=" €"
                    :hideZeroLabels="true"
                    information="Montant que vous percevez réellement pour chaque réservation. Il correspond au revenu net après prise en compte des frais liés à la gestion." />
                  <IndicatorCard
                    v-if="isGasGav"
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="CANCELLATION_BUSINESS_VOLUME"
                    label="Volume d'affaires annulations"
                    information="Montant total des loyers liés aux réservations annulées (hors ménage et taxe de séjour)."
                    positive-delta-is-bad
                    unit=" €" />
                </div>
                <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
                  <div
                    v-for="i in financeIndice"
                    :key="i"
                    class="h-2 w-2 rounded-full transition-colors duration-300"
                    :class="scrollIndices.finance === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
                </div>
              </div>
            </div>

            <div class="gap-4 flex flex-col">
              <span class="px-3 py-1 text-grey-900 text-[18px] font-semibold title-border secondary before:h-full">
                Occupation
              </span>
              <div class="relative">
                <div
                  class="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8 md:pb-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0"
                  @scroll="handleGroupScroll($event, 'occupation')">
                  <!--    CARDS      -->
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="OCCUPANCY_RATE"
                    label="Taux d’occupation"
                    unit=" %"
                    information="Part des nuits occupées sur votre calendrier (incluant les nuits louées et les nuits bloquées). Il donne une vision globale de l’utilisation de votre logement." />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="CLOSURE_RATE"
                    label="Taux de fermeture"
                    unit=" %"
                    positive-delta-is-bad
                    information="Part des nuits où votre logement n’est pas disponible à la réservation (bloqué ou fermé). Un taux élevé peut limiter vos opportunités de réservation." />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="GUEST_OCCUPANCY_RATE"
                    label="Taux d'occupation voyageur"
                    unit=" %"
                    information="Part des nuits réellement louées par des voyageurs, sur les nuits ouvertes à la réservation. C’est un indicateur clé de votre performance commerciale." />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="UNAVAILABLE_NIGHTS"
                    label="Nombre de nuits indisponibles"
                    unit=" nuits"
                    positive-delta-is-bad />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="AVAILABLE_NIGHTS"
                    label="Nombre de nuits disponibles"
                    unit=" nuits" />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="RENTED_NIGHTS"
                    label="Nombre de nuits louées"
                    unit=" nuits" />
                </div>
                <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="h-2 w-2 rounded-full transition-colors duration-300"
                    :class="scrollIndices.occupation === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
                </div>
              </div>
            </div>

            <div class="gap-4 flex flex-col">
              <span class="px-3 py-1 text-grey-900 text-[18px] font-semibold title-border secondary before:h-full">
                Réservations
              </span>
              <div class="relative">
                <div
                  class="flex items-center md:items-start overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8 md:pb-0 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0"
                  @scroll="handleGroupScroll($event, 'reservations')">
                  <!--    CARDS      -->
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="RESERVATION_COUNT"
                    :hideZeroLabelst="true"
                    label="Nombre de réservations" />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="AVERAGE_STAY_DURATION"
                    label="Durée de séjour moyenne"
                    :hideZeroLabelst="true"
                    information="Nombre moyen de nuits réservées par séjour."
                    unit=" nuits" />
                  <IndicatorCard
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="AVERAGE_BOOKING_WINDOW"
                    :hideZeroLabelst="true"
                    label="Fenêtre de réservation moyenne"
                    information="Délai moyen entre la réservation et l’arrivée des voyageurs."
                    unit=" jours" />
                  <IndicatorCard
                    v-if="isGasGav"
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="CANCELLATION_COUNT"
                    label="Nombre d’annulations"
                    :hideZeroLabelst="true"
                    positive-delta-is-bad />
                  <IndicatorCard
                    v-if="isGasGav"
                    class="snap-center shrink-0 w-full md:w-auto"
                    technicalKey="AVERAGE_CANCELLATION_WINDOW"
                    label="Fenêtre d’annulation moyenne"
                    information="Délai moyen entre l’annulation et la date d’arrivée."
                    :hideZeroLabelst="true"
                    unit=" jours" />
                  <SiteDistributionIndicatorCard class="snap-center shrink-0 w-full md:w-auto" />
                </div>
                <div class="flex justify-center gap-2 absolute bottom-0 left-0 right-0 md:hidden">
                  <div
                    v-for="i in reservationIndice"
                    :key="i"
                    class="h-2 w-2 rounded-full transition-colors duration-300"
                    :class="scrollIndices.reservations === i - 1 ? 'bg-primary' : 'bg-grey-300'"></div>
                </div>
              </div>
            </div>
          </IndicatorGroup>
        </AkShimmerGroup>
      </div>
    </div>
  </template>
  <ga-layout-empty-tab
    v-if="!this.hasAccommodation && isOwner"
    icon="ga-trend-up-graph"
    title="Mes __statistiques__"
    gradient="linear-gradient(109deg, rgba(96, 140, 232, 0.20) -0.71%, rgba(143, 179, 255, 0.00) 42.65%), #E9F0FF"
    background-color="#E9F0FF"
    @init="init">
    <div class="flex flex-col grow items-center md:gap-8 gap-4 md:p-4 p-8 pb-0">
      <div class="border-[7px] border-jordy-300 rounded-full flex items-center justify-center w-[70px] h-[70px]">
        <i class="ga-icon ga-trend-up-graph text-[30px] text-grey-900" />
      </div>
      <div class="flex flex-col gap-6 items-center">
        <div class="text-base text-grey-900 text-center">
          <div class="font-semibold text-lg">Retrouvez ici vos statistiques !</div>
          Publiez votre logement sur Guestadom et découvrez vos premières statistiques !
        </div>
        <AkButtonValidation
          class="btn-primary !h-[48px] w-fit !px-8 font-medium"
          @click="goToOnboarding"
          ref="onboardingButton"
          :label="hasPendingOnboarding ? 'Reprendre la publication' : 'Démarrer la publication'" />
      </div>
    </div>
    <img v-if="$isMobile()" src="@refonte/img/emptyTabs/stats_mobile_illustration.png" alt="" class="w-full" />
    <div v-else class="h-full md:max-w-[55%] w-full flex items-end justify-end">
      <img src="@refonte/img/emptyTabs/stats_illustration.png" alt="" class="h-auto object-cover object-left" />
    </div>
  </ga-layout-empty-tab>
</template>

<style scoped>
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.logo-site {
  background-size: 32px;
}
</style>
